import React, { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { getOpportunity, applyToOpportunity, cancelApplication, db } from "@/api/db";
import {
  ArrowLeft, Briefcase, MapPin, DollarSign, Clock, FileText,
  Globe, ChevronRight, Check, X, AlertTriangle, Send, Building2,
  BarChart3, CalendarDays, Info, User
} from "lucide-react";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatCurrency(value) {
  if (!value) return null;
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

const STATE_ABBR = {
  "Acre": "AC", "Alagoas": "AL", "Amapá": "AP", "Amazonas": "AM",
  "Bahia": "BA", "Ceará": "CE", "Distrito Federal": "DF", "Espírito Santo": "ES",
  "Goiás": "GO", "Maranhão": "MA", "Mato Grosso": "MT", "Mato Grosso do Sul": "MS",
  "Minas Gerais": "MG", "Pará": "PA", "Paraíba": "PB", "Paraná": "PR",
  "Pernambuco": "PE", "Piauí": "PI", "Rio de Janeiro": "RJ", "Rio Grande do Norte": "RN",
  "Rio Grande do Sul": "RS", "Rondônia": "RO", "Roraima": "RR", "Santa Catarina": "SC",
  "São Paulo": "SP", "Sergipe": "SE", "Tocantins": "TO",
};

function getStateAbbreviation(state) {
  if (!state) return null;
  if (STATE_ABBR[state]) return STATE_ABBR[state];
  const normalized = state.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  for (const [name, abbr] of Object.entries(STATE_ABBR)) {
    const nameNorm = name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    if (nameNorm === normalized) return abbr;
  }
  if (state.length === 2) return state.toUpperCase();
  return state;
}

function formatCityName(city) {
  if (!city) return "";
  const lowerCaseWords = ["de", "da", "das", "do", "dos", "e"];
  return city.toLowerCase().split(" ").map((word, i) =>
    lowerCaseWords.includes(word) && i !== 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)
  ).join(" ");
}

function formatSalaryRange(min, max) {
  if (!min && !max) return null;
  if (min && max) return `${formatCurrency(min)} - ${formatCurrency(max)}`;
  if (min) return `A partir de ${formatCurrency(min)}`;
  return `Até ${formatCurrency(max)}`;
}

const AVATAR_COLORS = ["#00664C", "#008572", "#00DCA4", "#65541B", "#892E38"];
function avatarColor(name) {
  return AVATAR_COLORS[(name?.charCodeAt(0) ?? 0) % AVATAR_COLORS.length];
}

// ─── Company Logo ─────────────────────────────────────────────────────────────

function CompanyLogo({ logoUrl, name, size = 70 }) {
  const [imgError, setImgError] = useState(false);
  if (logoUrl && !imgError) {
    return (
      <img src={logoUrl} alt={name} onError={() => setImgError(true)}
        className="rounded-full object-contain bg-white flex-shrink-0"
        style={{ width: size, height: size, border: "1px solid #E5E9EA" }} />
    );
  }
  return (
    <div className="rounded-full flex items-center justify-center flex-shrink-0"
      style={{ width: size, height: size, background: "#F2F4F4" }}>
      <Building2 className="w-8 h-8" style={{ color: "#99A6AB" }} />
    </div>
  );
}

// ─── Rich text renderer ──────────────────────────────────────────────────────

function RichText({ html }) {
  if (!html) return null;
  return (
    <div
      className="prose prose-sm max-w-none text-sm leading-relaxed"
      style={{ color: "#2E3233" }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

// ─── Additional questions modal ──────────────────────────────────────────────

function QuestionsModal({ questions, onSubmit, onClose, submitting }) {
  const [answers, setAnswers] = useState(() =>
    questions.map(q => ({ questionId: q.id, response: q.previousAnswer || "" }))
  );

  function updateAnswer(idx, value) {
    setAnswers(prev => prev.map((a, i) => i === idx ? { ...a, response: value } : a));
  }

  const answeredCount = answers.filter(a => (a.response || "").trim().length > 0).length;
  const totalCount = questions.length;
  const allAnswered = totalCount > 0 && answeredCount === totalCount;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.4)" }}>
      <div className="bg-white rounded-3xl w-full max-w-lg max-h-[90vh] flex flex-col"
        style={{ border: "1px solid #e2e8f0", boxShadow: "0 20px 60px rgba(0,0,0,0.15)" }}>
        {/* Header */}
        <div className="p-5 flex items-center justify-between" style={{ borderBottom: "1px solid #e2e8f0" }}>
          <h3 className="font-bold text-base" style={{ color: "#2E3233" }}>
            Perguntas adicionais
          </h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100">
            <X className="w-5 h-5" style={{ color: "#646C6F" }} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          <p className="text-sm" style={{ color: "#646C6F" }}>
            Tratam-se de informações extras que a empresa gostaria de saber sobre você.{" "}
            <strong>Preencha com bastante atenção!</strong>
          </p>
          {questions.map((q, idx) => (
            <div key={q.id}>
              <label className="block text-sm font-medium mb-1.5" style={{ color: "#2E3233" }}>
                {idx + 1}. {q.question}
              </label>
              <textarea
                rows={3}
                value={answers[idx]?.response ?? ""}
                onChange={e => updateAnswer(idx, e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg text-sm resize-none"
                style={{ border: "1px solid #E5E9EA", outline: "none", color: "#2E3233" }}
                placeholder="Sua resposta..."
              />
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="p-5 flex items-center justify-between" style={{ borderTop: "1px solid #e2e8f0" }}>
          <span className="text-xs" style={{ color: allAnswered ? "#008572" : "#99A6AB" }}>
            {answeredCount} de {totalCount} respondida{totalCount !== 1 ? "s" : ""}
          </span>
          <div className="flex gap-3">
            <button onClick={onClose}
              className="px-5 h-[44px] rounded-full text-sm font-medium"
              style={{ color: "#00664C", border: "1px solid #00664C" }}>
              Cancelar
            </button>
            <button
              onClick={() => onSubmit(answers)}
              disabled={!allAnswered || submitting}
              className="px-5 h-[44px] rounded-full text-sm font-bold flex items-center gap-2 transition-opacity"
              style={{
                background: allAnswered ? "#00DCA4" : "#E5E9EA",
                color: allAnswered ? "#2E3233" : "#99A6AB",
                border: allAnswered ? "1px solid #00DCA4" : "1px solid #E5E9EA",
                opacity: submitting ? 0.6 : 1,
              }}
            >
              {submitting ? "Enviando..." : "Candidatar-se"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function OpportunityDetail() {
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  const [opp, setOpp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showQuestions, setShowQuestions] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);
  const [profileIncomplete, setProfileIncomplete] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getOpportunity(id)
      .then(setOpp)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    db.profile.get()
      .then(p => {
        if (!p.addresses || !p.gender || (p.knowledge?.skills?.length === 0)) {
          setProfileIncomplete(true);
        }
      })
      .catch(() => {});
  }, []);

  async function handleApply(additionalAnswers) {
    setActionLoading(true);
    setActionMsg(null);
    try {
      const result = await applyToOpportunity(opp.id, additionalAnswers);
      setOpp(prev => ({ ...prev, application: result }));
      setShowQuestions(false);
      setActionMsg({ type: "success", text: "Candidatura enviada com sucesso!" });
    } catch (err) {
      setActionMsg({ type: "error", text: err.message });
    } finally {
      setActionLoading(false);
    }
  }

  async function handleCancel() {
    if (!confirm("Tem certeza que deseja desistir desta vaga?")) return;
    setActionLoading(true);
    setActionMsg(null);
    try {
      await cancelApplication(opp.id);
      setOpp(prev => ({ ...prev, application: { ...prev.application, status: "CANCELED" } }));
      setActionMsg({ type: "success", text: "Candidatura cancelada." });
    } catch (err) {
      setActionMsg({ type: "error", text: err.message });
    } finally {
      setActionLoading(false);
    }
  }

  function onApplyClick() {
    if (opp.additionalQuestions?.length > 0) {
      setShowQuestions(true);
    } else {
      handleApply([]);
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="max-w-[1440px] mx-auto w-full">
        <div className="animate-pulse space-y-6">
          <div className="h-5 w-32 rounded-full" style={{ background: "#E5E9EA" }} />
          <div className="h-8 w-2/3 rounded-full" style={{ background: "#E5E9EA" }} />
          <div className="flex flex-col md:flex-row gap-6">
            <div className="w-full md:w-2/5 space-y-4">
              <div className="h-60 rounded-3xl" style={{ background: "#E5E9EA" }} />
            </div>
            <div className="w-full md:w-3/5 space-y-4">
              <div className="h-40 rounded-3xl" style={{ background: "#E5E9EA" }} />
              <div className="h-32 rounded-3xl" style={{ background: "#E5E9EA" }} />
              <div className="h-32 rounded-3xl" style={{ background: "#E5E9EA" }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !opp) {
    return (
      <div className="max-w-[1440px] mx-auto w-full">
        <div className="rounded-3xl p-12 flex flex-col items-center gap-3 text-center bg-white"
          style={{ border: "1px solid #e2e8f0" }}>
          <AlertTriangle className="w-10 h-10" style={{ color: "#F59E0B" }} />
          <p className="text-sm font-medium" style={{ color: "#2E3233" }}>{error || "Vaga não encontrada"}</p>
          <Link to={createPageUrl("Opportunities")}
            className="text-sm font-semibold mt-2" style={{ color: "#00664C" }}>
            Voltar para vagas
          </Link>
        </div>
      </div>
    );
  }

  const salary = formatSalaryRange(opp.minSalary, opp.maxSalary);
  const appStatus = opp.application?.status;
  const canApply = !appStatus || appStatus === "CANCELED";
  const canCancel = appStatus && ["NEW", "LIKED", "INTERVIEW"].includes(appStatus);
  const isHired = appStatus === "HIRED";
  const isRejected = appStatus === "REJECTED";
  const isCanceled = appStatus === "CANCELED";
  const isSubscribed = appStatus && !isCanceled;

  const locationStr = opp.regions
    ?.map(r => {
      if (r.city && r.state) return `${formatCityName(r.city)}, ${getStateAbbreviation(r.state)}`;
      if (r.state) return getStateAbbreviation(r.state);
      return null;
    })
    .filter(Boolean)
    .join(" | ") || "Todas as regiões";

  return (
    <div className="max-w-[1440px] mx-auto w-full">
      {/* Back link */}
      <Link to={createPageUrl("Opportunities")}
        className="inline-flex items-center gap-1.5 text-sm font-medium" style={{ color: "#00664C" }}>
        <ArrowLeft className="w-4 h-4" /> Voltar para oportunidades
      </Link>

      <div className="my-4" style={{ borderTop: "1px solid #e2e8f0" }} />

      {/* Title row + Action buttons */}
      <div className="flex flex-col gap-4 md:gap-0 md:flex-row md:justify-between md:items-center">
        <h1 className="text-xl md:text-2xl font-bold" style={{ color: "#2E3233" }}>
          Detalhes da vaga
        </h1>

        <div className="flex flex-col gap-2 md:flex-row md:items-center">
          {/* Status info text */}
          {isSubscribed && !isHired && !isRejected && (
            <div className="flex gap-2 items-center mr-4">
              <Info className="w-4 h-4 flex-shrink-0" style={{ color: "#646C6F" }} />
              <span className="text-sm" style={{ color: "#646C6F" }}>
                Você já está inscrito nessa vaga.
              </span>
            </div>
          )}
          {isCanceled && (
            <div className="flex gap-2 items-center mr-4">
              <Info className="w-4 h-4 flex-shrink-0" style={{ color: "#646C6F" }} />
              <span className="text-sm" style={{ color: "#646C6F" }}>
                Você cancelou sua inscrição nessa vaga.
              </span>
            </div>
          )}

          {/* Action buttons */}
          {canApply && (
            profileIncomplete ? (
              <Link
                to="/ProfileSettings"
                className="h-[44px] px-6 rounded-full text-sm font-bold flex items-center justify-center gap-2 no-underline"
                style={{ background: "#E5E9EA", color: "#646C6F", border: "1px solid #E5E9EA" }}
              >
                <User className="w-4 h-4" />
                Complete seu perfil para candidatar-se
              </Link>
            ) : (
              <button
                onClick={onApplyClick}
                disabled={actionLoading}
                className="h-[44px] px-6 rounded-full text-sm font-bold flex items-center justify-center gap-2 transition-opacity"
                style={{ background: "#00DCA4", color: "#2E3233", border: "1px solid #00DCA4", opacity: actionLoading ? 0.6 : 1 }}
              >
                {actionLoading ? "Enviando..." : "Candidatar-se"}
              </button>
            )
          )}

          {isSubscribed && (
            <button
              onClick={canCancel ? handleCancel : undefined}
              disabled={actionLoading || isHired || isRejected}
              className="h-[44px] px-6 rounded-full text-sm font-medium flex items-center justify-center gap-2 transition-opacity"
              style={{
                background: "#FADBDF",
                color: isHired || isRejected ? "#99A6AB" : "#892E38",
                border: "1px solid #FADBDF",
                opacity: actionLoading ? 0.6 : 1,
                cursor: isHired || isRejected ? "not-allowed" : "pointer",
              }}
            >
              <X className="w-4 h-4" />
              {isCanceled ? "Cancelada" : "Desistir da vaga"}
            </button>
          )}
        </div>
      </div>

      <div className="my-4" style={{ borderTop: "1px solid #e2e8f0" }} />

      {/* Action message */}
      {actionMsg && (
        <div className="rounded-xl px-4 py-3 mb-4 text-sm font-medium flex items-center gap-2"
          style={{
            background: actionMsg.type === "success" ? "#D6F5E6" : "#FADBDF",
            color: actionMsg.type === "success" ? "#008572" : "#892E38",
          }}>
          {actionMsg.type === "success" ? <Check className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
          {actionMsg.text}
        </div>
      )}

      {/* 2-column layout: Company LEFT (sticky) + Content RIGHT */}
      <div className="flex flex-col md:flex-row gap-4 md:gap-6 w-full">
        {/* LEFT: Company card (sticky) */}
        <div className="w-full md:w-2/5 md:sticky self-start h-fit" style={{ top: 80 }}>
          <div className="rounded-3xl p-5 md:p-6" style={{ background: "rgba(255,255,255,0.7)", border: "1px solid #e2e8f0" }}>
            <div className="flex gap-3 items-center">
              <CompanyLogo logoUrl={opp.logoUrl} name={opp.companyName} size={70} />
              <div>
                <h2 className="text-base font-bold" style={{ color: "#2E3233" }}>
                  {opp.isConfidential ? "Empresa confidencial" : opp.companyName}
                </h2>
              </div>
            </div>

            {/* About company */}
            {!opp.isConfidential && opp.companyAbout && (
              <>
                <div className="my-4" style={{ borderTop: "1px solid #e2e8f0" }} />
                <h3 className="text-sm font-bold mb-2" style={{ color: "#2E3233" }}>Sobre a empresa</h3>
                <RichText html={opp.companyAbout} />
              </>
            )}

            {/* Company links */}
            {!opp.isConfidential && (opp.companyWebsite || opp.companyInstagram) && (
              <>
                <div className="my-4" style={{ borderTop: "1px solid #e2e8f0" }} />
                <div className="flex items-center justify-center gap-6">
                  {opp.companyWebsite && (
                    <button
                      type="button"
                      onClick={() => window.open(opp.companyWebsite, "_blank")}
                      className="flex flex-col gap-1 items-center"
                    >
                      <Globe className="w-5 h-5" style={{ color: "#00664C" }} />
                      <span className="text-xs font-medium" style={{ color: "#00664C" }}>Website</span>
                    </button>
                  )}
                  {opp.companyInstagram && (
                    <button
                      type="button"
                      onClick={() => window.open(opp.companyInstagram, "_blank")}
                      className="flex flex-col gap-1 items-center"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="#00664C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                      </svg>
                      <span className="text-xs font-medium" style={{ color: "#00664C" }}>Instagram</span>
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* RIGHT: Job details */}
        <div className="flex flex-col gap-4 flex-grow w-full md:w-3/5">
          {/* About Job Opening card */}
          <div className="rounded-3xl p-5 md:p-6" style={{ background: "rgba(255,255,255,0.7)", border: "1px solid #e2e8f0" }}>
            <div className="flex justify-between items-start">
              <h2 className="text-base font-bold" style={{ color: "#2E3233" }}>
                {opp.title}
              </h2>
              {isHired && (
                <span className="text-xs px-2 py-1 rounded font-medium flex-shrink-0"
                  style={{ background: "#D6F5E6", color: "#008572" }}>
                  Contratado
                </span>
              )}
              {isRejected && (
                <span className="text-xs px-2 py-1 rounded font-medium flex-shrink-0"
                  style={{ background: "#2E3233", color: "#E5E9EA" }}>
                  Reprovado
                </span>
              )}
            </div>

            <div className="my-4 block md:hidden" style={{ borderTop: "1px solid #e2e8f0" }} />
            <div className="mt-4" />

            {/* Info items in horizontal flex-wrap (like the free platform) */}
            <div className="flex gap-4 flex-wrap">
              {opp.jobModel && (
                <div className="flex gap-2 items-center">
                  <Briefcase className="w-5 h-5" style={{ color: "#2E3233" }} />
                  <span className="text-sm" style={{ color: "#646C6F" }}>{opp.jobModel}</span>
                </div>
              )}
              {opp.availability && (
                <div className="flex gap-2 items-center">
                  <Clock className="w-5 h-5" style={{ color: "#2E3233" }} />
                  <span className="text-sm" style={{ color: "#646C6F" }}>{opp.availability}</span>
                </div>
              )}
              {opp.contractModel && (
                <div className="flex gap-2 items-center">
                  <FileText className="w-5 h-5" style={{ color: "#2E3233" }} />
                  <span className="text-sm" style={{ color: "#646C6F" }}>{opp.contractModel}</span>
                </div>
              )}
              {opp.seniority && (
                <div className="flex gap-2 items-center">
                  <BarChart3 className="w-5 h-5" style={{ color: "#2E3233" }} />
                  <span className="text-sm" style={{ color: "#646C6F" }}>{opp.seniority}</span>
                </div>
              )}
              {salary && (
                <div className="flex gap-2 items-center">
                  <DollarSign className="w-5 h-5" style={{ color: "#2E3233" }} />
                  <span className="text-sm" style={{ color: "#646C6F" }}>{salary}</span>
                </div>
              )}
            </div>

            {/* Location (hidden if remote) */}
            {locationStr && opp.jobModel !== "Remoto" && (
              <>
                <div className="mt-4" />
                <div className="flex gap-2 items-center">
                  <MapPin className="w-5 h-5" style={{ color: "#2E3233" }} />
                  <span className="text-sm" style={{ color: "#646C6F" }}>{locationStr}</span>
                </div>
              </>
            )}

            {/* Application closing date */}
            {opp.applicationClosingDate && (
              <>
                <div className="mt-4" />
                {(() => {
                  const closingDate = new Date(opp.applicationClosingDate);
                  const isExpired = closingDate < new Date();
                  const formatted = closingDate.toLocaleDateString("pt-BR");
                  return (
                    <div className="flex gap-2 items-center">
                      <CalendarDays className="w-5 h-5" style={{ color: isExpired ? "#892E38" : "#2E3233" }} />
                      <span className="text-sm" style={{ color: isExpired ? "#892E38" : "#646C6F" }}>
                        Término das inscrições: {formatted}
                      </span>
                    </div>
                  );
                })()}
              </>
            )}
          </div>

          {/* Content sections with divider between title and content */}
          {opp.description && (
            <Section title="Descrição da vaga">
              <RichText html={opp.description} />
            </Section>
          )}

          {opp.responsibilities && (
            <Section title="Responsabilidades">
              <RichText html={opp.responsibilities} />
            </Section>
          )}

          {opp.requirements && (
            <Section title="Requisitos técnicos">
              <RichText html={opp.requirements} />
            </Section>
          )}

          {opp.differentials && (
            <Section title="Diferenciais">
              <RichText html={opp.differentials} />
            </Section>
          )}

          {opp.benefits && opp.benefits.length > 0 && (
            <Section title="Benefícios">
              <div className="flex flex-wrap gap-3">
                {opp.benefits.map((b, i) => (
                  <span key={i} className="text-sm px-4 py-1.5 rounded-full font-medium"
                    style={{ background: "#61E9C7", color: "#00664C" }}>
                    {b}
                  </span>
                ))}
              </div>
            </Section>
          )}

          <div className="h-6" />
        </div>
      </div>

      {/* Questions modal */}
      {showQuestions && (
        <QuestionsModal
          questions={opp.additionalQuestions}
          onSubmit={handleApply}
          onClose={() => setShowQuestions(false)}
          submitting={actionLoading}
        />
      )}
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function Section({ title, children }) {
  return (
    <div className="rounded-3xl p-5 md:p-6" style={{ background: "rgba(255,255,255,0.7)", border: "1px solid #e2e8f0" }}>
      <h2 className="text-base font-bold" style={{ color: "#2E3233" }}>
        {title}
      </h2>
      <div className="my-4" style={{ borderTop: "1px solid #e2e8f0" }} />
      {children}
    </div>
  );
}
